import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MessageStatus, OutreachContact } from "schemas/dashboard";

import { useOutreachTable } from "features/Influencer/ContactList/useOutreachTable";
import { sanitize } from "utils/sanitize";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

interface Props {
  contact: OutreachContact;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const ScheduledContact = ({ contact }: Props) => {
  const { isSmallScreen } = useOutreachTable();
  const { currentContact, setCurrentContact, fetchScheduledEmails } =
    useContext(OutreachContactsContext);
  const navigate = useNavigate();
  const earliestPendingEmail = contact.emails?.find(
    (email) => email.status === MessageStatus.PENDING,
  );

  const formattedDate = formatEpochTimestamp({
    epochTimestamp: Number(earliestPendingEmail?.sendAt),
    fullDate: true,
  });

  const isFailed =
    !!earliestPendingEmail?.sendAt &&
    Date.now() - Number(earliestPendingEmail.sendAt) * 1000 > ONE_DAY_MS;

  const subject = earliestPendingEmail
    ? earliestPendingEmail?.subject
    : contact?.lastGmailMessageSubject?.replace(/(<([^>]+)>)/gi, "");

  const snippet = `${
    earliestPendingEmail?.body
      ? sanitize(earliestPendingEmail.body)
          .replace(/<\/?[^>]*>/g, " ")
          .substring(0, 170)
      : ""
  }...`;

  const onClick = () => {
    trackEvent("Inbox Table Row Clicked", {
      contactId: contact?.id,
      lastGmailThreadId: contact.lastGmailThreadId,
    });

    const isScheduled =
      !contact.latestSequenceSentCount && contact.latestSequenceNextSendAt;

    let url = isScheduled
      ? `${contact.id}`
      : contact?.lastGmailThreadId
        ? `${contact.id}/email/${contact.lastGmailThreadId}`
        : `${contact.id}`;

    fetchScheduledEmails(Number(contact.id));
    if (isSmallScreen) {
      setCurrentContact(contact);
    } else {
      navigate(url);
    }
  };

  return (
    <Card key={contact.id} elevation={0}>
      <CardActionArea
        onClick={onClick}
        sx={[
          {
            height: "100%",
          },
          currentContact?.id === contact.id
            ? { backgroundColor: "#f0f2ee" }
            : {},
        ]}
      >
        <CardContent sx={styles.section}>
          <Grid container alignItems="center" justifyContent={"space-between"}>
            <Typography sx={{ color: "primary.main" }}>
              <strong>{contact?.brand?.name}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={styles.formattedDate}
              component="div"
            >
              {formattedDate}
            </Typography>
          </Grid>

          {isFailed && (
            <Tooltip
              title="This email wasn't sent as scheduled. Open it to send now."
              enterTouchDelay={0}
            >
              <Chip
                label="Failed"
                size="small"
                color="error"
                sx={{
                  mt: 0.5,
                  fontWeight: 600,
                  alignSelf: "flex-start",
                }}
              />
            </Tooltip>
          )}

          <Typography sx={{ fontSize: { xs: 11, md: 13 } }}>
            <strong>{subject}</strong>
          </Typography>

          <Typography
            component="div"
            dangerouslySetInnerHTML={{
              __html: snippet,
            }}
            sx={styles.snippet}
          ></Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ScheduledContact;
